import { defineStore } from 'pinia'
import { getFreeCredits, getUserInfo, logout } from '~/composables/api/user.js'
import { syncAuthSessionHintCookie } from '~/composables/useAuthSessionHint'
import { DEFAULT_DAILY_BONUS_AMOUNT } from '~/constants/dailyBonus.js'
import { syncUserVideoTasks } from '~/utils/taskSync.js'

const DAILY_BONUS_STATUS = {
  SUCCESS: 'SUCCESS',
  PENDING: 'PENDING',
}

let freeCreditsClaimPromise = null

function firstDefined(values) {
  return values.find((v) => v !== undefined && v !== null && v !== '')
}

function normalizeDailyBonusStatus(value) {
  const status = String(value ?? '').trim().toUpperCase()
  if (status === DAILY_BONUS_STATUS.PENDING) return DAILY_BONUS_STATUS.PENDING
  if (status === DAILY_BONUS_STATUS.SUCCESS) return DAILY_BONUS_STATUS.SUCCESS
  return ''
}

function readDailyBonusStatus(data = {}) {
  const ui = data?.user_info || {}
  const status = normalizeDailyBonusStatus(
    firstDefined([
      data?.daily_bonus_status,
      data?.dailyBonusStatus,
      ui?.daily_bonus_status,
      ui?.dailyBonusStatus,
    ]),
  )
  if (status) return status
  if (data?.daily_grant === true || data?.daily_grant === false) {
    return DAILY_BONUS_STATUS.SUCCESS
  }
  return ''
}

function readDailyBonusAmount(data = {}, fallback = DEFAULT_DAILY_BONUS_AMOUNT) {
  const ui = data?.user_info || {}
  const raw = firstDefined([
    data?.daily_credits_grant,
    data?.daily_bonus_amount,
    data?.free_credits_grant,
    data?.free_credits_add,
    data?.credits_add,
    data?.amount,
    ui?.daily_credits_grant,
    ui?.daily_bonus_amount,
  ])
  const n = Number(raw)
  return Number.isFinite(n) && n > 0 ? Math.floor(n) : fallback
}

function normalizeCreditNumber(value) {
  const n = Number(value)
  return Number.isFinite(n) ? Math.max(0, n) : 0
}

/** 全站用户会话与登录相关 UI 状态（单一定义，避免与 `stores/` 下其它 demo 撞名） */
export const useUserStore = defineStore('user', {
  persist: {
    omit: [
      'login_modal_show',
      'price_modal_show',
      'check_modal_show',
      'daily_bonus_notice_nonce',
      'daily_bonus_notice_amount',
    ],
  },
  state: () => {
    return {
      token: '',
      userName: '',
      /** 展示用昵称（如谷歌 name）；无则头像字母取自邮箱 */
      displayName: '',
      credits: 0,
      /** 与后端字段对齐；缺省时由 getUserInfo 从 total 推导展示 */
      free_credits: 0,
      paid_credits: 0,
      daily_bonus_status: DAILY_BONUS_STATUS.SUCCESS,
      daily_bonus_amount: DEFAULT_DAILY_BONUS_AMOUNT,
      daily_bonus_notice_nonce: 0,
      daily_bonus_notice_amount: 0,
      login_modal_show: false,
      price_modal_show: false,
      kodepay_user_id: '',
      has_paid_credits: false,
      check_modal_show: false,
      /** 未登录访客标识，用于上传等接口 authorization */
      uuid: '',
    }
  },
  getters: {
    getToken() {
      return this.token
    },
  },
  actions: {
    setToken(token) {
      this.token = token
      if (import.meta.client) {
        syncAuthSessionHintCookie(token)
      }
    },
    setUserName(userName) {
      this.userName = userName
    },
    setDisplayName(name) {
      this.displayName = name || ''
    },
    setFreeCredits(n) {
      this.free_credits = Math.max(0, Number(n) || 0)
    },
    setPaidCredits(n) {
      this.paid_credits = Math.max(0, Number(n) || 0)
    },
    setCredits(credits) {
      this.credits = credits
    },
    setDailyBonusStatus(status) {
      this.daily_bonus_status =
        normalizeDailyBonusStatus(status) || DAILY_BONUS_STATUS.SUCCESS
    },
    setDailyBonusAmount(amount) {
      this.daily_bonus_amount = readDailyBonusAmount({ amount }, this.daily_bonus_amount)
    },
    notifyDailyBonusClaimed(amount) {
      this.daily_bonus_notice_amount = readDailyBonusAmount(
        { amount },
        DEFAULT_DAILY_BONUS_AMOUNT,
      )
      this.daily_bonus_notice_nonce += 1
    },
    setLoginModalShow(flag) {
      this.login_modal_show = flag
    },
    setPriceModalShow(flag) {
      this.price_modal_show = flag
    },
    setCheckModalShow(flag) {
      this.check_modal_show = flag
    },
    setKodepayUserId(id) {
      this.kodepay_user_id = id
    },
    setHasPaidCredits(flag) {
      this.has_paid_credits = flag
    },
    setUuid(uuid) {
      this.uuid = uuid || ''
    },
    async logout(router) {
      const { code, data, message } = await logout()
      this.setToken('')
      this.setCredits(0)
      this.setUserName('')
      this.setDisplayName('')
      this.setFreeCredits(0)
      this.setPaidCredits(0)
      this.setDailyBonusStatus(DAILY_BONUS_STATUS.SUCCESS)
      this.setDailyBonusAmount(DEFAULT_DAILY_BONUS_AMOUNT)
      this.setKodepayUserId('')
      this.setUuid('')
      localStorage.setItem('user_id', '')
      localStorage.setItem('token', '')
      setTimeout(() => {
        location.reload()
      }, 1000)
    },
    applyCreditSnapshot(data = {}, options = {}) {
      const ui = data.user_info || {}
      const creditsRaw = firstDefined([data.credits, ui.credits])
      const freeRaw = firstDefined([
        data.free_credits,
        data.freeCredits,
        ui.free_credits,
        ui.freeCredits,
      ])
      const paidRaw = firstDefined([
        data.pay_credits,
        data.paid_credits,
        data.paidCredits,
        ui.pay_credits,
        ui.paid_credits,
        ui.paidCredits,
      ])

      const hasCredits = creditsRaw !== undefined && creditsRaw !== null && creditsRaw !== ''
      const hasFree = freeRaw !== undefined && freeRaw !== null && freeRaw !== ''
      const hasPaid = paidRaw !== undefined && paidRaw !== null && paidRaw !== ''

      if (hasFree && hasPaid) {
        this.setFreeCredits(freeRaw)
        this.setPaidCredits(paidRaw)
      } else if (hasFree) {
        this.setFreeCredits(freeRaw)
        if (hasCredits) {
          this.setPaidCredits(Math.max(0, normalizeCreditNumber(creditsRaw) - this.free_credits))
        }
      } else if (hasPaid) {
        this.setPaidCredits(paidRaw)
        if (hasCredits) {
          this.setFreeCredits(Math.max(0, normalizeCreditNumber(creditsRaw) - this.paid_credits))
        }
      } else if (hasCredits) {
        const credits = normalizeCreditNumber(creditsRaw)
        if (options.deriveFreeFromExistingPaid) {
          this.setFreeCredits(Math.max(0, credits - this.paid_credits))
        } else if (options.missingBreakdownAsPaid) {
          this.setFreeCredits(0)
          this.setPaidCredits(credits)
        } else {
          this.setCredits(credits)
          return true
        }
      } else {
        return false
      }

      this.setCredits(this.free_credits + this.paid_credits)
      return true
    },
    async getUserInfo() {
      if (!this.token) return
      const { code, data, message } = await getUserInfo()
      if (code === 100000) {
        const ui = data.user_info || {}
        const token = data.token
        const user_id = ui.user_id ?? data.user_id
        const user_email = ui.user_email ?? data.user_email
        const display =
          ui.display_name ||
          ui.name ||
          ui.nickname ||
          ui.google_name ||
          data.display_name ||
          ''
        this.setToken(token)
        this.setUserName(user_email || ui.user_email || '')
        this.setDisplayName(display)
        if (!this.applyCreditSnapshot(data, { missingBreakdownAsPaid: true })) {
          this.setFreeCredits(0)
          this.setPaidCredits(0)
          this.setCredits(0)
        }
        this.setDailyBonusStatus(readDailyBonusStatus(data))
        this.setDailyBonusAmount(readDailyBonusAmount(data, this.daily_bonus_amount))
        this.login_modal_show = false
        this.setKodepayUserId(data.kodepay_user_id)
        this.setHasPaidCredits(data.has_paid_credits)
        localStorage.setItem('user_id', user_id != null ? String(user_id) : '')
        localStorage.setItem('token', token)
        if (
          localStorage.getItem('showCheckInModal') !== 'closed' &&
          !this.has_paid_credits
        ) {
          setTimeout(() => {
            this.setCheckModalShow(true)
          }, 2000)
        }
        void syncUserVideoTasks().catch(() => {})
      } else {
        this.setToken('')
        this.setCredits(0)
        this.setUserName('')
        this.setDisplayName('')
        this.setFreeCredits(0)
        this.setPaidCredits(0)
        this.setDailyBonusStatus(DAILY_BONUS_STATUS.SUCCESS)
        this.setDailyBonusAmount(DEFAULT_DAILY_BONUS_AMOUNT)
        this.setKodepayUserId('')
        localStorage.setItem('user_id', '')
        localStorage.setItem('token', '')
      }
    },
    async claimDailyFreeCredits() {
      if (!this.token) return { claimed: false }
      if (this.daily_bonus_status !== DAILY_BONUS_STATUS.PENDING) {
        return { claimed: false }
      }
      if (freeCreditsClaimPromise) return freeCreditsClaimPromise

      freeCreditsClaimPromise = (async () => {
        const res = await getFreeCredits()
        const { code, data = {}, message } = res || {}
        if (code !== 100000) {
          return { claimed: false, code, message }
        }

        const amount = readDailyBonusAmount(
          data,
          this.daily_bonus_amount || DEFAULT_DAILY_BONUS_AMOUNT,
        )
        const appliedCredits = this.applyCreditSnapshot(data, {
          deriveFreeFromExistingPaid: true,
        })

        if (!appliedCredits) {
          this.setFreeCredits(this.free_credits + amount)
          this.setCredits(this.free_credits + this.paid_credits)
        }

        this.setDailyBonusStatus(DAILY_BONUS_STATUS.SUCCESS)
        this.setDailyBonusAmount(amount)
        if (data.has_paid_credits !== undefined) {
          this.setHasPaidCredits(data.has_paid_credits)
        }
        this.notifyDailyBonusClaimed(amount)
        return { claimed: true, amount, data }
      })()

      try {
        return await freeCreditsClaimPromise
      } finally {
        freeCreditsClaimPromise = null
      }
    },
  },
})
